import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { Loader } from "../../../UI/Loaders/Loaders";
import { useDispatch } from "react-redux";
import moment from "moment";
import { showModal } from "../../../redux/actions/modal";
import useAllAdminUsers, { IAdminUser } from "../hooks/useAllAdminUsers";
import useAllRoles from "../../../custom-hooks/useAllRoles";
import { useState } from "react";
import {
  postDataWithDotNet,
  putDataWithDotNet,
} from "../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../api/ajax-endpoints";
import { errorHandler } from "../../../helpers/errorHandler";
import Paginator from "../../Pagination/Pagination.component";
import { appInsights } from "../../../config/appInsights";
import CustomSelectDropdown from "../../CustomHTMLElements/CustomSelectDropdown";

type TAddUser = {
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  roleIds: number[];
};

const ManageAdminUsers = () => {
  const [loading, setLoading] = useState<null | string>(null);
  const [success, setSuccess] = useState<null | string>(null);
  const [error, setError] = useState<null | string>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [size, setSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [roleId, setRoleId] = useState(0);
  const [searchByEmailQuery, setSearchByEmailQuery] = useState("");
  const [userType, setUserType] = useState("");

  const dispatch = useDispatch();

  const {
    data,
    error: dataError,
    status,
  } = useAllAdminUsers({
    role_id: roleId.toString(),
    Page: pageNumber.toString(),
    PageSize: size.toString(),
    search_value: searchByEmailQuery,
    user_type: userType,
  });

  const {
    data: rolesData,
    error: rolesError,
    status: rolesStatus,
  } = useAllRoles();

  //  Methods
  const modalHandler = (modalData: any) => {
    dispatch(
      showModal({
        modalIdentifier: "createuser",
        dataRecord: modalData,
        action: "customrange",
      }),
    );
  };

  const handleAdvancedSearch = ({
    roleId: newRoleId,
    userType: newUserType,
  }: {
    roleId: number;
    userType: string;
  }) => {
    setRoleId(newRoleId);
    setUserType(newUserType);
    setPageNumber(1);
  };

  const handleClearAdvancedSearch = () => {
    setRoleId(0);
    setUserType("");
    setPageNumber(1);
  };

  const handleSearch = () => {
    setPageNumber(1);
    setSearchByEmailQuery(searchValue);
  };

  const updateUser = async (reqBody: any) => {
    setLoading("Updating User...");
    setError(null);
    try {
      await putDataWithDotNet(ajaxEndpoints.MANAGE_ADMIN_USER, reqBody);
      setLoading(null);
      setSuccess("User has been updated.");

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "ManageAdminUsers.component.jsx",
        },
      });
      setLoading(null);
      setError(errorHandler(error));
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };

  const addUser = async (reqBody: TAddUser) => {
    setLoading("Adding new user...");
    setError(null);
    try {
      await postDataWithDotNet(ajaxEndpoints.MANAGE_ADMIN_USER, reqBody);
      setLoading(null);
      setSuccess("New user has been added.");
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "ManageAdminUsers.component.jsx",
        },
      });
      setLoading(null);
      // setError(errorHandler(error));
      setError(error?.response?.data?.data[0]);
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };

  const updateUserRoles = async (reqBody: any) => {
    setLoading("Updating user roles...");
    setError(null);
    try {
      const response = await postDataWithDotNet(
        ajaxEndpoints.ASSIGN_ROLE,
        reqBody,
      );

      setLoading(null);
      dispatch(
        showModal({
          modalIdentifier: "feedback",
          dataRecord: {
            type: "success",
            message: "New roles have been added to this user.",
          },
        }),
      );
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "ManageAdminUsers.component.jsx",
        },
      });
      setLoading(null);
      dispatch(
        showModal({
          modalIdentifier: "feedback",
          dataRecord: {
            type: "failure",
            message: error?.response?.data,
          },
        }),
      );
    }
  };

  const resetTable = () => {
    setPageNumber(1);
  };

  const getRowMenuItems = (user: IAdminUser): MenuProps["items"] => [
    { key: "view", label: "View" },
    { key: "update", label: "Update" },
    { key: "update_role", label: "Update Role(s)" },
  ];

  const handleRowMenuClick =
    (user: IAdminUser) =>
    ({ key }: { key: string }) => {
      if (key === "view") {
        modalHandler({
          type: "view_user",
          ssoUserId: user.sso_user_id,
        });
      } else if (key === "update") {
        modalHandler({ type: "update_user", user, action: updateUser });
      } else if (key === "update_role") {
        modalHandler({
          type: "update_user_role",
          data: { rolesData, user },
          action: updateUserRoles,
        });
      }
    };

  let RenderedComponent = null;
  if (
    (!data && status !== "loading") ||
    (!rolesData && rolesStatus !== "loading")
  ) {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Admin Users
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading" || rolesStatus === "loading") {
    RenderedComponent = <Loader centered={true} text="Loading users" />;
  } else if (loading) {
    RenderedComponent = <Loader centered={true} text={loading} />;
  } else if (error) {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt p-y-5">
            <i className="fas fa-bell" /> {error}
          </h4>
        </div>
      </div>
    );
  } else {
    if (rolesError || dataError) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              Admin Users. Please Reload The Page.
            </h4>
          </div>
        </div>
      );
    } else if (data && data?.data.length === 0) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> No users available
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <MDBTable responsive minimal="true" bordered hover>
          <MDBTableHead>
            <tr>
              <th>S/N</th>
              <th>Email</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>User Type</th>
              <th>Phone Number</th>
              <th>Date Joined</th>
              <th colSpan={2}>Last Login</th>
              <th>Manage</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {data &&
              data?.data?.length &&
              data?.data.map((user: IAdminUser, _id: number) => {
                const {
                  email,
                  user_type,
                  date_joined,
                  first_name,
                  last_name,
                  last_login,
                  phone,
                } = user;
                return (
                  <tr key={_id}>
                    <td>{_id + 1}</td>
                    <td>{email}</td>
                    <td>{first_name}</td>
                    <td>{last_name}</td>
                    <td>{user_type}</td>
                    <td>{phone}</td>
                    <td>{moment(date_joined).format("llll")}</td>
                    <td colSpan={2}>{moment(last_login).format("llll")}</td>
                    <td>
                      <Dropdown
                        menu={{
                          items: getRowMenuItems(user),
                          onClick: handleRowMenuClick(user),
                        }}
                        trigger={["click"]}
                      >
                        <button className="btn btn-sm" type="button">
                          <MoreOutlined />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
          </MDBTableBody>
        </MDBTable>
      );
    }
  }

  return (
    <div className="datatable-wrapper">
      <div className="table-view speed-1x animated fadeInRight">
        <div className="d-flex flex-row flex-wrap justify-content-between mb-4">
          <h3 className="page-title">Admin Users</h3>
          <button
            className="btn advancly-btn m-0"
            onClick={() => {
              modalHandler({
                type: "add_user",
                rolesData,
                action: addUser,
              });
            }}
          >
            <i className="fas fa-user-plus" /> &nbsp; Create User
          </button>
        </div>

        <div className="row">
          <div className="form-group col-md-3">
            <label>Search by email</label>
            <input
              className="form-control"
              type="text"
              placeholder="Enter email address"
              aria-label="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <div className="form-group col-md-2">
            <label>Results per page</label>
            <CustomSelectDropdown
              name="pageSize"
              onChange={(e) => {
                setSize(Number(e.target.value));
                resetTable();
              }}
              value={size}
              isShowDropdownArrow
            >
              <option value={10}>10 Entries</option>
              <option value={20}>20 Entries</option>
              <option value={50}>50 Entries</option>
              <option value={100}>100 Entries</option>
            </CustomSelectDropdown>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <button
              className="btn advancly-white-btn ml-2 form-control"
              onClick={() => {
                dispatch(
                  showModal({
                    modalIdentifier: "advancedadminusersearch",
                    dataRecord: {
                      onApply: handleAdvancedSearch,
                      onClear: handleClearAdvancedSearch,
                    },
                  }),
                );
              }}
            >
              <i className="fas fa-search" />
              &nbsp; Advanced Search
            </button>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <button
              className="btn advancly-btn form-control"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
          <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
            <button
              type="button"
              className="btn advancly-red-btn btn-md form-control"
              onClick={() => {
                setSearchValue("");
                setSearchByEmailQuery("");
                handleClearAdvancedSearch();
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div>{RenderedComponent}</div>

        <Paginator
          size={size}
          page={pageNumber}
          count={data?.totalCount}
          changeCurrentPage={(pageNumber: any) => setPageNumber(pageNumber)}
        />
      </div>
    </div>
  );
};

export default ManageAdminUsers;
